import {
  ArrowUp,
  Check,
  ChevronDown,
  ClipboardCheck,
  FileInput,
  type LucideIcon,
  Plus,
  Sparkles,
  Workflow,
} from 'lucide-react';
import { useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { errorMessageKey } from '../api/api-error';
import { paths } from '../app/paths';
import type { InitialMessageState } from '../assistant/use-initial-message';
import { Menu, MenuContent, MenuItem, MenuSeparator, MenuTrigger } from '../components/ui/menu';
import type { DiagramItem } from '../diagrams/diagrams-api';
import { useI18n } from '../i18n/i18n';
import type { MessageKey } from '../i18n/en';
import { cn } from '../lib/cn';

/** Кнопки нижнего ряда поля и быстрые предложения — одна «пилюля» прототипа. */
const PILL =
  'inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full border border-border bg-transparent px-3 text-xs text-foreground outline-none transition-colors hover:bg-tint focus-visible:ring-2 focus-visible:ring-primary/40 data-[popup-open]:bg-tint';

const TEXTAREA_MAX_PX = 200;

/** Быстрые предложения (docs/saas/сценарии.md §1): подпись плашки и формулировка в поле. */
const SUGGESTIONS: ReadonlyArray<{ label: MessageKey; text: MessageKey; icon: LucideIcon }> = [
  { label: 'home.suggest.check', text: 'home.suggest.checkText', icon: ClipboardCheck },
  { label: 'home.suggest.scratch', text: 'home.suggest.scratchText', icon: Sparkles },
  { label: 'home.suggest.files', text: 'home.suggest.filesText', icon: FileInput },
];

/** Имя новой схемы из первой строки сообщения — до 60 символов. */
export function nameFromMessage(text: string, fallback: string): string {
  const firstLine = text.trim().split('\n')[0]?.trim() ?? '';
  if (firstLine === '') return fallback;
  return firstLine.length > 60 ? `${firstLine.slice(0, 59).trimEnd()}…` : firstLine;
}

interface ChatStartProps {
  workspaceId: string;
  diagrams: readonly DiagramItem[];
  canCreate: boolean;
  /** Список схем ещё грузится — в меню выбора строка «Загрузка…». */
  loading?: boolean;
  createDiagram: (name: string) => Promise<DiagramItem>;
}

/**
 * Чат-старт домашнего экрана (docs/saas/screens/блок-схемы.md): сообщение уходит в новый чат
 * выбранной схемы, а если схема не выбрана — создаёт новую (имя — первая строка). Сообщение
 * передаётся экрану схемы состоянием перехода и отправляется там.
 */
export function ChatStart({
  workspaceId,
  diagrams,
  canCreate,
  loading = false,
  createDiagram,
}: ChatStartProps) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const [targetId, setTargetId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const field = useRef<HTMLTextAreaElement>(null);
  // Выбранную схему удалили — выбор сбрасывается на «новую».
  const target = targetId === null ? null : (diagrams.find((item) => item.id === targetId) ?? null);

  useLayoutEffect(() => {
    const element = field.current;
    if (element === null) return;
    element.style.height = 'auto';
    element.style.height = `${Math.min(element.scrollHeight, TEXTAREA_MAX_PX)}px`;
  }, [text]);

  const submit = async () => {
    const message = text.trim();
    if (message === '' || busy) return;
    if (target === null && !canCreate) {
      setError(t('home.cannotCreate'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const diagram = target ?? (await createDiagram(nameFromMessage(message, t('home.untitled'))));
      const state: InitialMessageState = { initialMessage: message };
      await navigate(paths.diagram(workspaceId, diagram.id), { state });
    } catch (caught) {
      setError(t(errorMessageKey(caught)));
      setBusy(false);
    }
  };

  const pick = (phrase: string) => {
    setText(phrase);
    setError(null);
    field.current?.focus();
  };

  return (
    <section className={cn(busy && 'pointer-events-none opacity-60')}>
      <h1 className="mb-4 text-center text-xl font-semibold tracking-tight">{t('home.title')}</h1>
      <div
        className="mx-auto flex w-full max-w-3xl cursor-text flex-col rounded-[28px] bg-card shadow-[0_4px_4px_0_#0000000a,0_0_1px_0_#0000009e]"
        onClick={(event) => {
          if (!(event.target as HTMLElement).closest('button')) field.current?.focus();
        }}
      >
        <div className="px-5 pt-4 pb-1">
          <textarea
            ref={field}
            value={text}
            rows={1}
            autoFocus
            aria-label={t('home.chatLabel')}
            placeholder={t('home.placeholder')}
            onChange={(event) => {
              setText(event.target.value);
              setError(null);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
                event.preventDefault();
                void submit();
              }
            }}
            className="block w-full resize-none bg-transparent text-base leading-6 outline-none placeholder:text-hint"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2 px-3 pb-3">
          <Menu>
            <MenuTrigger>
              <button
                type="button"
                aria-label={t('home.diagramSelect')}
                className={cn(PILL, 'max-w-xs')}
              >
                <Workflow className="size-3.5 shrink-0 text-muted-foreground" />
                <span className="truncate">{target?.name ?? t('home.newDiagram')}</span>
                <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" />
              </button>
            </MenuTrigger>
            <MenuContent className="max-h-80 w-80 overflow-auto">
              {canCreate && (
                <MenuItem onClick={() => setTargetId(null)}>
                  <Plus />
                  <span className="min-w-0 flex-1 truncate">{t('home.newDiagram')}</span>
                  {target === null && <Check className="size-3.5! text-primary" />}
                </MenuItem>
              )}
              {canCreate && diagrams.length > 0 && <MenuSeparator />}
              {diagrams.map((diagram) => (
                <MenuItem key={diagram.id} onClick={() => setTargetId(diagram.id)}>
                  <Workflow />
                  <span className="min-w-0 flex-1 truncate">{diagram.name}</span>
                  {diagram.id === target?.id && <Check className="size-3.5! text-primary" />}
                </MenuItem>
              ))}
              {loading && (
                <div className="px-2.5 py-2 text-xs text-muted-foreground">
                  {t('common.loading')}
                </div>
              )}
            </MenuContent>
          </Menu>
          <button
            type="button"
            aria-label={t('assistant.send')}
            disabled={text.trim() === '' || busy}
            onClick={() => void submit()}
            className="ml-auto inline-flex size-8 items-center justify-center rounded-full bg-control text-control-foreground transition-[opacity,filter] outline-none hover:brightness-95 focus-visible:ring-2 focus-visible:ring-primary/40 disabled:opacity-40 [&_svg]:size-[18px]"
          >
            <ArrowUp />
          </button>
        </div>
      </div>
      <div
        role="group"
        aria-label={t('home.quickAria')}
        className="mx-auto mt-3 flex max-w-3xl flex-wrap justify-center gap-2"
      >
        {SUGGESTIONS.map((suggestion) => {
          const phrase = t(suggestion.text);
          const on = text.trim() === phrase;
          const Icon = suggestion.icon;
          return (
            <button
              key={suggestion.label}
              type="button"
              aria-pressed={on}
              onClick={() => pick(phrase)}
              className={cn(
                PILL,
                on && 'border-primary/50 bg-accent text-accent-foreground hover:bg-accent',
              )}
            >
              <Icon className={cn('size-3.5', on ? 'text-primary' : 'text-muted-foreground')} />
              {t(suggestion.label)}
            </button>
          );
        })}
      </div>
      {error !== null && (
        <p className="mx-auto mt-2 max-w-3xl px-5 text-center text-xs text-danger">{error}</p>
      )}
    </section>
  );
}
